import React, { FC } from 'react';
import { CHANNEL_SCALE, PROJECT_QUESTIONS } from './constants';
import { EntityMatrix, MatrixRow } from './EntityMatrix';
import { QuestionList } from './QuestionList';
import { EntityType, ProjectSection, Scores } from './types';

interface Props {
    project: ProjectSection;
    position: number;
    total: number;
    scores: Scores;
    comment: string;
    entityScore: (entityType: EntityType, entityId: number) => number | null;
    onScore: (field: string, value: number) => void;
    onComment: (value: string) => void;
    onEntityScore: (
        entityType: EntityType,
        entityId: number,
        value: number,
    ) => void;
    readOnly?: boolean;
}

export const ProjectStep: FC<Props> = ({
    project,
    position,
    total,
    scores,
    comment,
    entityScore,
    onScore,
    onComment,
    onEntityScore,
    readOnly,
}) => {
    const rows: MatrixRow[] = project.channels.map((channel) => {
        const [onlyAsset] = channel.assets;

        if (channel.assets.length === 1) {
            return {
                key: `channel-${channel.id}`,
                label: `${channel.name} · ${onlyAsset.name}`,
                value:
                    entityScore('asset', onlyAsset.id)
                    ?? entityScore('channel', channel.id),
                onChange: (value) => {
                    onEntityScore('asset', onlyAsset.id, value);
                    onEntityScore('channel', channel.id, value);
                },
            };
        }

        return {
            key: `channel-${channel.id}`,
            label: channel.name,
            value: entityScore('channel', channel.id),
            onChange: (value) => onEntityScore('channel', channel.id, value),
            children: channel.assets.map((asset) => ({
                key: `asset-${asset.id}`,
                label: asset.name,
                value: entityScore('asset', asset.id),
                onChange: (value) => onEntityScore('asset', asset.id, value),
            })),
        };
    });

    return (
        <>
            {!readOnly && (
                <div className="pb-5 mb-[22px] border-b border-border-token">
                    <span className="inline-flex items-center gap-[5px] text-[11px] font-bold px-2.5 py-[3px] rounded-md uppercase tracking-[0.4px] text-[#A07B2E] bg-[#FAF5ED]">
                        {`Project ${position} of ${total}`}
                        {project.content_type ? ` · ${project.content_type}` : ''}
                    </span>
                    <h2 className="text-[20px] font-bold text-text-1 mt-2.5 mb-0 tracking-[-0.2px]">
                        {project.title}
                    </h2>
                </div>
            )}

            <QuestionList
                questions={PROJECT_QUESTIONS}
                scores={scores}
                commentLabel="Additional comments on this project"
                commentPlaceholder="Anything you'd like to add…"
                comment={comment}
                onScore={onScore}
                onComment={onComment}
                readOnly={readOnly}
            />

            {rows.length > 0 && (
                <div className="mt-6 pt-[22px] border-t border-border-token">
                    <div className="mb-[14px]">
                        <span className="block text-[14.5px] font-semibold text-text-1">
                            Satisfaction by channel &amp; format
                        </span>
                        <span className="block text-[12px] text-muted mt-[3px]">
                            {`1 = ${CHANNEL_SCALE.lowLabel} · 5 = ${CHANNEL_SCALE.highLabel} · optional`}
                        </span>
                    </div>
                    <EntityMatrix rows={rows} readOnly={readOnly} />
                </div>
            )}
        </>
    );
};
